import React, { useState } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { verifyFillInBlanks } from '../../../common/verifyFillInBlanks'; // Adjust the path to your verify script
import SpeechButton from '../../common/SpeechButton'; // Import the SpeechButton component

interface QuestionPreviewProps {
  question: {
    id: string;
    text: string;
    hint: string;
    answers: string[][];
    voiceCode?: string; // Add voiceCode to support individual voice settings
  };
  isContinuous: boolean;
  onAnswerChange: (questionId: string, answer: string) => void;
  isCorrect?: boolean; // Optional prop to show correct/incorrect icons
}

const QuestionPreview: React.FC<QuestionPreviewProps> = ({
  question,
  isContinuous,
  onAnswerChange,
  isCorrect
}) => {
  const [answer, setAnswer] = useState('');

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(e.target.value);
    onAnswerChange(question.id, e.target.value);
  };

  const answerBox = (
    <TextField
      variant="standard"
      size="small"
      sx={{ 
        width: '150px', 
        marginX: 0.5,
        '& .MuiInput-root': {
          fontSize: 'inherit',
        },
        '& .MuiInput-input': {
          textAlign: 'center',
          padding: '0 4px',
        },
        '& .MuiInput-underline:before': {
          borderBottomColor: 'rgba(0, 0, 0, 0.42)',
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderBottomColor: 'rgba(0, 0, 0, 0.87)',
        },
      }}
      value={answer}
      onChange={handleAnswerChange}
    />
  );

  // Split hints if they're combined with pipe separator (continuous mode)
  const hints = question.hint ? question.hint.split(' | ') : [];

  const formattedText = question.text.split('__').reduce((acc, part, index, array) => {
    acc.push(<span key={`text-${index}`}>{part}</span>);
    if (index < array.length - 1) {
      acc.push(<span key={`input-${index}`}>{answerBox}</span>);
      // Add corresponding hint after each blank
      if (hints[index]) {
        acc.push(
          <Typography 
            key={`hint-${index}`} 
            component="span" 
            variant="caption" 
            color="textSecondary" 
            sx={{ ml: 0.5, fontStyle: 'italic' }}
          >
            ({hints[index]})
          </Typography>
        );
      }
    }
    return acc;
  }, [] as React.ReactNode[]);

  return (
    <Box mb={isContinuous ? 0 : 2}>
      <Box display="flex" alignItems="baseline" flexWrap="wrap">
        <Typography variant="body1" component="div" sx={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap' }}>
          {formattedText}
        </Typography>
        {isCorrect !== undefined && (
          <Box ml={1} display="inline-flex" alignItems="center">
            {isCorrect ? <CheckCircleIcon color="success" fontSize="small" /> : <CancelIcon color="error" fontSize="small" />}
          </Box>
        )}
        <Box ml={1} display="inline-flex">
          <SpeechButton 
            id={question.id} 
            text={question.text.replace(/__/g, answer)} 
            voiceCode={question.voiceCode || 'en-US-JennyNeural'} // Use question's voiceCode or default to Jenny
          />
        </Box>
      </Box>
    </Box>
  );
};

interface FillInBlanksPreviewProps {
  config: {
    questions: {
      id: string;
      text: string;
      answers: string[][];
      hint: string;
      aiCheckEnabled?: boolean;
      voiceCode?: string; // Add voiceCode property here
    }[];
    isContinuous: boolean;
  };
}

const FillInBlanksPreview: React.FC<FillInBlanksPreviewProps> = ({ config }) => {
  const [userAnswers, setUserAnswers] = useState<{ [key: string]: string }>({});
  const [results, setResults] = useState<{ questionId: string; isCorrect: boolean }[]>([]);

  const handleAnswerChange = (questionId: string, answer: string) => {
    setUserAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleCheckAnswers = () => {
    const answersArray = Object.entries(userAnswers).map(([questionId, answer]) => ({
      questionId,
      answer,
    }));

    const verificationResults = verifyFillInBlanks(answersArray, {
      exerciseType: 'fill_in_the_blanks',
      language: 'fi',
      isContinuous: config.isContinuous,
      questions: config.questions.map(q => ({
        id: q.id,
        text: q.text,
        answers: q.answers,
        hint: q.hint,
        aiCheckEnabled: q.aiCheckEnabled ?? false,
        voiceCode: q.voiceCode || "en-US-JennyNeural" // Provide a default value
      })),      
    });

    setResults(verificationResults);
  };

  return (
    <Box mt={2}>
      {config.isContinuous ? (
        <QuestionPreview
          question={{
            id: 'continuous',
            text: config.questions.map(q => q.text).join(' '),
            answers: config.questions.flatMap(q => q.answers),
            hint: config.questions.map(q => q.hint).join(' | '),
            voiceCode: config.questions[0]?.voiceCode // Use the first question's voiceCode as default for continuous mode
          }}
          isContinuous={true}
          onAnswerChange={handleAnswerChange}
        />
      ) : (
        config.questions.map((question, index) => {
          const result = results.find(res => res.questionId === question.id);
          return (
            <QuestionPreview
              key={index}
              question={question}
              isContinuous={false}
              onAnswerChange={handleAnswerChange}
              isCorrect={result?.isCorrect}
            />
          );
        })
      )}

      <Box mt={2}>
        <Button variant="contained" onClick={handleCheckAnswers}>
          Tarkista vastaukset
        </Button>
      </Box>
    </Box>
  );
};

export default FillInBlanksPreview;
